type LocalStorageKey = 'brax.user.grid-type' | 'brax.web-app.tour.complete' | 'brax.user.language' | 'brax.user.notifications';

type SessionStorageKey = 'brax.web-app.tour.skipped' | 'brax.web-app.street-view.image-editor.hint-skkiped';

type FlagLocalStorageKey = never;

type FlagSessionStorageKey = never;

type FlagStorageKey = FlagLocalStorageKey | FlagSessionStorageKey;

type StorageKey = LocalStorageKey | SessionStorageKey | FlagStorageKey;

const _get = <T = string>(storage: Storage, key: StorageKey, json: boolean, throwError: boolean): T | null => {
    try {
        const value = storage[key];

        if (json) {
            if (value)
                try {
                    return JSON.parse(value);
                } catch (_) {
                    console.error(`Failed to parse value from ${storage === sessionStorage ? 'Session' : 'Local'}Storage (key: "${key}"). Original value:`, value);
                }

            return null
        }

        return value;
    } catch (error) {
        console.error(`Failed to get from ${storage === sessionStorage ? 'Session' : 'Local'}Storage (key: "${key}")`);
        console.error(error);

        if (throwError)
            throw error;
    }

    return null;
};

const _set = (storage: Storage, key: StorageKey, value: any, json: boolean, throwError: boolean) => {
    try {
        if (value === null)
            delete storage[key];
        else
            storage[key] = json ? JSON.stringify(value) : value;
    } catch (error) {
        console.error(`Failed to put in ${storage === sessionStorage ? 'Session' : 'Local'}Storage (key: "${key}")`);
        console.error(error);

        if (throwError)
            throw error;
    }
};

const get = (key: LocalStorageKey, throwError: boolean = false) => _get(localStorage, key, false, throwError);

const getJSON = <T = any>(key: LocalStorageKey, throwError: boolean = false) => _get<T>(localStorage, key, true, throwError);

const set = (key: LocalStorageKey, value: any, throwError: boolean = false) => _set(localStorage, key, value, false, throwError);

const setJSON = (key: LocalStorageKey, value: any, throwError: boolean = false) => _set(localStorage, key, value, true, throwError);

const getSession = (key: SessionStorageKey, throwError: boolean = false) => _get(sessionStorage, key, false, throwError);

const getSessionJSON = <T = any>(key: SessionStorageKey, throwError: boolean = false) => _get<T>(sessionStorage, key, true, throwError);

const setSession = (key: SessionStorageKey, value: any, throwError: boolean = false) => _set(sessionStorage, key, value, false, throwError);

const setSessionJSON = (key: SessionStorageKey, value: any, throwError: boolean = false) => _set(sessionStorage, key, value, true, throwError);

const _checkTimeoutFlag = (storage: Storage, key: FlagStorageKey, days: number, throwError: boolean) => {
    const lastCheck = _get(storage, key, true, throwError);

    const now = Date.now();

    if (!lastCheck) {
        _set(storage, key, now, true, throwError);

        return true;
    }

    const timestamp = new Date(lastCheck).getTime();

    if (Number.isNaN(timestamp) || now - timestamp > days * 24 * 60 * 60 * 1000) {
        _set(storage, key, now, true, throwError);
    
        return true;
    }

    return false;
};

const checkTimeoutFlag = (key: FlagLocalStorageKey, days: number = 1, throwError: boolean = false) => _checkTimeoutFlag(localStorage, key, days, throwError);

const checkTimeoutFlagSession = (key: FlagSessionStorageKey, days: number = 1, throwError: boolean = false) => _checkTimeoutFlag(sessionStorage, key, days, throwError);

const session = { get: getSession, getJSON: getSessionJSON, set: setSession, setJSON: setSessionJSON, checkTimeoutFlag: checkTimeoutFlagSession };

const storage = { get, getJSON, set, setJSON, checkTimeoutFlag, session };

export default storage;
